import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Gift, Sparkles, Coins, Trophy, Ticket, Percent, Gem, Star, Loader2 } from "lucide-react";
import { fetchRewardsState, openLootBox } from "../lib/api";
import { useAuth } from "../lib/auth";
import CategoryTabs from "../components/CategoryTabs";
import EvoTokenBar from "../components/EvoTokenBar";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/recompensas";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const RARITY_STYLE = {
  común:  { color: "#a3a3a3", ring: "rgba(163,163,163,0.5)" },
  raro:   { color: "#60a5fa", ring: "rgba(96,165,250,0.7)" },
  épico:  { color: "#c084fc", ring: "rgba(192,132,252,0.85)" },
};

function iconFor(key) {
  if (key === "diamonds_110") return Gem;
  if (key === "booyah_pass") return Trophy;
  if (key === "discount_5") return Percent;
  if (key.startsWith("evo")) return Sparkles;
  return Gift;
}

export default function Recompensas() {
  const { user, loading, refresh } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(true);
  const [opening, setOpening] = useState(false);
  const [revealed, setRevealed] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const s = await fetchRewardsState();
      setState(s);
    } catch (e) {
      // 401 handled below
      setState(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!loading && user) load();
  }, [loading, user]);

  const points = state?.points ?? 0;
  const cost = state?.cost_per_box ?? 10;
  const canOpen = points >= cost && !opening;

  async function handleOpen() {
    if (!user) {
      startGoogleLogin();
      return;
    }
    if (!canOpen) return;
    setOpening(true);
    setRevealed(null);
    try {
      // Wait for animation before revealing
      const spinPromise = new Promise((r) => setTimeout(r, 1800));
      const [res] = await Promise.all([openLootBox(), spinPromise]);
      setRevealed(res.reward);
      setState((s) => (s ? { ...s, points: res.points_remaining } : s));
      refresh?.();
      toast.success(`¡Ganaste ${res.reward.prize_label}!`);
      // Refresh inventory
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error abriendo la caja";
      toast.error(msg);
    } finally {
      setOpening(false);
    }
  }

  if (loading) return null;
  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <Gift className="w-14 h-14 text-amber-400 mx-auto mb-4" />
        <h1 className="font-display text-4xl tracking-wider">Iniciá sesión para ganar recompensas</h1>
        <p className="text-neutral-400 mt-2 text-sm">
          Sumás puntos con cada recarga de diamantes y los canjeás por cajas sorpresa.
        </p>
        <button onClick={startGoogleLogin} data-testid="rewards-login-btn" className="btn-primary mt-6 px-5 py-3 rounded-md">
          Iniciar con Google
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen">

      <CategoryTabs active="recompensas" />

      {/* Hero */}
      <section className="relative overflow-hidden pt-10 sm:pt-14 pb-8">
        <div className="absolute -top-20 left-0 sm:-left-20 w-64 h-64 sm:w-[420px] sm:h-[420px] rounded-full bg-amber-400/15 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] rounded-full bg-[#ff4500]/15 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/40 text-amber-300 text-xs tracking-widest mb-4">
            <Trophy className="w-3.5 h-3.5" /> PROGRAMA DE LEALTAD
          </div>
          <h1 className="font-display text-5xl sm:text-6xl leading-[0.9] tracking-wider text-white">
            Cajas <span className="text-amber-300">Sorpresa</span>
          </h1>
          <p className="mt-3 text-neutral-300 text-base sm:text-lg max-w-2xl">
            Ganás puntos con cada recarga de diamantes. Canjeálos por cajas con premios aleatorios.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Points balance + box */}
          <div className="lg:col-span-2 rounded-2xl border border-amber-400/30 bg-gradient-to-br from-[#0d0d0d] via-black to-[#180d02] p-6 relative overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-400">Tus puntos</div>
                <div className="flex items-baseline gap-2">
                  <div className="font-display text-6xl tracking-wider text-white" data-testid="points-balance">{points}</div>
                  <Star className="w-6 h-6 text-amber-300 fill-amber-300" />
                </div>
                <div className="text-xs text-neutral-500 mt-1">Cada caja cuesta <span className="text-amber-300 font-bold">{cost} pts</span></div>
              </div>
              <Link to="/" data-testid="earn-points-link" className="text-sm px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-neutral-200">
                Recargar diamantes →
              </Link>
            </div>

            {/* Loot box + animation */}
            <div className="grid place-items-center py-6">
              <LootBoxVisual opening={opening} revealed={revealed} />
            </div>

            <div className="mt-6 flex flex-col items-center gap-3">
              {revealed && !opening && (
                <button
                  onClick={() => setRevealed(null)}
                  data-testid="reset-reveal-btn"
                  className="text-xs text-neutral-500 hover:text-white"
                >
                  Volver a la caja
                </button>
              )}
              <button
                onClick={handleOpen}
                disabled={!canOpen}
                data-testid="open-box-btn"
                className="btn-primary px-8 py-4 rounded-md text-base flex items-center gap-2 disabled:cursor-not-allowed"
              >
                {opening ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Abriendo...
                  </>
                ) : points < cost ? (
                  <>Necesitás {cost - points} puntos más</>
                ) : (
                  <>
                    <Gift className="w-5 h-5" /> Abrir caja ({cost} pts)
                  </>
                )}
              </button>
              {points < cost && (
                <Link to="/" className="text-xs text-amber-300 underline">
                  Recargá diamantes para sumar más puntos
                </Link>
              )}
            </div>
          </div>

          {/* Prizes table */}
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-5">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Posibles premios</div>
            <div className="font-display text-2xl tracking-wider mt-1 mb-4">¿Qué podés ganar?</div>
            <div className="space-y-2">
              {(state?.prizes || []).map((p) => {
                const Ic = iconFor(p.key);
                const rs = RARITY_STYLE[p.rarity] || RARITY_STYLE["común"];
                return (
                  <div
                    key={p.key}
                    className="flex items-center gap-3 rounded-lg border border-white/5 bg-black/40 p-2.5"
                  >
                    <div
                      className="w-9 h-9 rounded-md grid place-items-center shrink-0"
                      style={{ border: `1px solid ${rs.ring}`, background: "#0a0a0a" }}
                    >
                      <Ic className="w-5 h-5" style={{ color: rs.color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white truncate">{p.label}</div>
                      <div className="text-[10px] uppercase tracking-widest" style={{ color: rs.color }}>{p.rarity}</div>
                    </div>
                    <div className="text-xs text-neutral-400">{p.chance_pct}%</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 text-[11px] text-neutral-500 leading-relaxed">
              Los premios se entregan manualmente por nuestro equipo tras el canje. Podés seguir el estado abajo.
            </div>
          </div>
        </div>

        {/* Points reference */}
        <div className="mt-8 rounded-xl border border-white/10 bg-[#0d0d0d] p-5">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Cómo ganás puntos</div>
          <div className="font-display text-2xl tracking-wider mb-3">1 punto por cada 110 diamantes (aprox)</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-sm">
            {[
              [110, 1], [341, 2], [572, 4], [1166, 8], [2398, 12], [6160, 15],
            ].map(([d, p]) => (
              <div key={d} className="rounded-md border border-white/5 bg-black/40 p-3 text-center">
                <div className="font-display text-xl tracking-wider text-white">{d.toLocaleString("es-AR")}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Diamantes</div>
                <div className="mt-1 text-amber-300 font-bold flex items-center justify-center gap-1">
                  <Star className="w-3 h-3 fill-amber-300" /> +{p}
                </div>
              </div>
            ))}
          </div>
        </div>

        <EvoTokenBar tokens={state?.evo_tokens ?? 0} goal={state?.evo_goal ?? 50} onClaimed={load} />

        {/* Inventory */}
        <div className="mt-8">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="text-xs tracking-[0.3em] text-amber-300">HISTORIAL</div>
              <h2 className="font-display text-3xl tracking-wider">Tus premios</h2>
            </div>
          </div>
          {busy ? (
            <div className="text-neutral-400">Cargando...</div>
          ) : (state?.inventory || []).length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-[#0d0d0d] p-8 text-center text-neutral-400">
              Todavía no abriste ninguna caja. ¡Guardá puntos y sumá premios!
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {state.inventory.map((r) => {
                const Ic = iconFor(r.prize_key);
                const rs = RARITY_STYLE[r.rarity] || RARITY_STYLE["común"];
                return (
                  <div
                    key={r.reward_id}
                    data-testid={`inventory-${r.reward_id}`}
                    className="rounded-lg border border-white/10 bg-[#0d0d0d] p-3 flex items-center gap-3"
                  >
                    <div className="w-11 h-11 rounded-md grid place-items-center shrink-0" style={{ border: `1px solid ${rs.ring}` }}>
                      <Ic className="w-6 h-6" style={{ color: rs.color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-sm truncate">{r.prize_label}</div>
                      <div className="text-[10px] text-neutral-500">
                        {new Date(r.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <div className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-md ${r.status === "pending_delivery" ? "bg-amber-400/15 text-amber-300" : "bg-emerald-500/20 text-emerald-400"}`}>
                      {r.status === "delivered" ? "Entregado" : r.status === "accumulated" ? "Sumado" : "Pendiente"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LootBoxVisual({ opening, revealed }) {
  const showReveal = !!revealed && !opening;
  const rs = revealed ? RARITY_STYLE[revealed.rarity] || RARITY_STYLE["común"] : null;
  const Ic = revealed ? iconFor(revealed.prize_key) : Gift;

  return (
    <div className="relative w-[280px] sm:w-[340px] h-[300px] sm:h-[340px] grid place-items-center">
      {/* Radial glow */}
      <motion.div
        className="absolute inset-0 rounded-full blur-3xl pointer-events-none"
        animate={{
          background: opening
            ? "radial-gradient(circle, rgba(251,191,36,0.75), rgba(255,69,0,0.55) 45%, transparent 70%)"
            : showReveal
              ? `radial-gradient(circle, ${rs.ring}, rgba(255,69,0,0.3) 45%, transparent 70%)`
              : "radial-gradient(circle, rgba(251,191,36,0.35), rgba(255,69,0,0.2) 50%, rgba(0,0,0,0) 75%)",
        }}
        transition={{ duration: 0.6 }}
      />

      {/* Rays behind chest */}
      {opening && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0, scale: 0.6, rotate: 0 }}
          animate={{ opacity: [0.2, 0.9, 1], scale: [0.6, 1.1, 1.2], rotate: [0, 30, 60] }}
          transition={{ duration: 1.8, ease: "easeOut" }}
          style={{
            background:
              "conic-gradient(from 0deg, transparent 0deg, rgba(251,191,36,0.6) 20deg, transparent 40deg, transparent 90deg, rgba(255,69,0,0.5) 110deg, transparent 130deg, transparent 180deg, rgba(251,191,36,0.5) 200deg, transparent 220deg, transparent 270deg, rgba(255,69,0,0.5) 290deg, transparent 310deg)",
            filter: "blur(6px)",
            maskImage: "radial-gradient(circle, black 30%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(circle, black 30%, transparent 70%)",
          }}
        />
      )}

      <AnimatePresence mode="wait">
        {!showReveal && (
          <motion.div
            key="chest"
            initial={{ opacity: 0, scale: 0.6, y: 20 }}
            animate={
              opening
                ? {
                    opacity: 1,
                    scale: [1, 1.05, 0.95, 1.08, 0.98, 1.1],
                    rotate: [0, -6, 6, -8, 8, -4, 4, 0],
                    y: [0, -8, 4, -10, 6, -4, 2, 0],
                  }
                : {
                    opacity: 1,
                    scale: 1,
                    y: [0, -6, 0],
                    rotate: [0, -1.5, 1.5, 0],
                  }
            }
            exit={{ opacity: 0, scale: 0.4, y: -20 }}
            transition={{
              duration: opening ? 1.8 : 4,
              repeat: opening ? 0 : Infinity,
              ease: "easeInOut",
            }}
            className="relative"
          >
            <img
              src="/chest.png"
              alt="Cofre RECARGAS al Instante"
              data-testid="loot-chest-image"
              className="w-[260px] sm:w-[320px] h-auto select-none pointer-events-none"
              style={{
                filter: opening
                  ? "drop-shadow(0 0 25px rgba(251,191,36,0.9)) drop-shadow(0 0 60px rgba(255,69,0,0.6))"
                  : "drop-shadow(0 12px 30px rgba(0,0,0,0.7)) drop-shadow(0 0 20px rgba(255,69,0,0.4))",
              }}
              draggable={false}
            />
            {/* Sparkles around chest */}
            {opening &&
              [...Array(14)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute rounded-full"
                  style={{
                    left: `${8 + ((i * 47) % 90)}%`,
                    top: `${5 + ((i * 29) % 85)}%`,
                    width: 4 + (i % 3) * 3,
                    height: 4 + (i % 3) * 3,
                    background: i % 2 === 0 ? "#fbbf24" : "#ff8a00",
                    boxShadow: "0 0 8px currentColor",
                    color: i % 2 === 0 ? "#fbbf24" : "#ff8a00",
                  }}
                  animate={{
                    opacity: [0, 1, 0],
                    scale: [0, 1.6, 0],
                    y: [0, -30, -60],
                  }}
                  transition={{
                    duration: 1.4,
                    delay: (i * 0.09) % 1.2,
                    repeat: Infinity,
                  }}
                />
              ))}
          </motion.div>
        )}

        {showReveal && (
          <motion.div
            key="prize"
            initial={{ opacity: 0, scale: 0.3, rotate: -180 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 180, damping: 14 }}
            className="relative flex flex-col items-center gap-3"
          >
            <div
              className="w-32 h-32 rounded-2xl grid place-items-center"
              style={{
                background: `linear-gradient(135deg, ${rs.color}22, #0a0a0a 80%)`,
                border: `3px solid ${rs.color}`,
                boxShadow: `0 0 40px ${rs.ring}, inset 0 0 15px ${rs.ring}`,
              }}
            >
              <Ic className="w-16 h-16" style={{ color: rs.color, filter: `drop-shadow(0 0 10px ${rs.ring})` }} />
            </div>
            <div className="text-center">
              <div className="text-xs uppercase tracking-widest" style={{ color: rs.color }}>
                {revealed.rarity}
              </div>
              <div className="font-display text-3xl tracking-wider text-white">
                {revealed.prize_label}
              </div>
              <div className="text-xs text-neutral-500 mt-1">Pendiente de entrega · Te contactaremos</div>
            </div>
            {/* Confetti */}
            {[...Array(24)].map((_, i) => {
              const c = ["#ff4500", "#fbbf24", "#22c55e", "#60a5fa", "#c084fc"][i % 5];
              return (
                <motion.div
                  key={i}
                  className="absolute w-2 h-3 rounded-sm"
                  style={{ background: c, left: "50%", top: "40%" }}
                  initial={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
                  animate={{
                    opacity: [1, 1, 0],
                    x: (Math.random() - 0.5) * 280,
                    y: (Math.random() - 0.2) * 240,
                    rotate: Math.random() * 720,
                  }}
                  transition={{ duration: 1.6, delay: i * 0.02, ease: "easeOut" }}
                />
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
